import { SCOPE_PERSISTENT, SCOPE_SESSION } from './dedupe.js';
import { createLogger } from './logger.js';

/**
 * The public surface: one place that builds an envelope, applies deduplication and
 * hands the event to the transport.
 *
 * Other plugins (the dnkqz quiz, nf-platform) cooperate by dispatching a
 * `trackdaddy:event` CustomEvent with `{ name, params, options }` — they never need a
 * reference to this object.
 */

export const INTAKE_EVENT = 'trackdaddy:event';

export class Tracker {
  constructor({
    context,
    session,
    dedupe,
    transport,
    envelope,
    logger = createLogger(false, {}),
    win = typeof window !== 'undefined' ? window : null,
  }) {
    this.context = context;
    this.session = session;
    this.dedupe = dedupe;
    this.transport = transport;
    this.envelope = envelope;
    this.logger = logger;
    this.win = win;
  }

  /**
   * @param {string} name
   * @param {Object} params
   * @param {{dedupeKey?: string, once?: 'session'|'persistent'}} options
   * @return {boolean} whether the event was handed to the transport
   */
  track(name, params = {}, options = {}) {
    if (!name || typeof name !== 'string') {
      return false;
    }

    const key = options.dedupeKey || null;
    const scope = options.once ? (options.once === 'persistent' ? SCOPE_PERSISTENT : SCOPE_SESSION) : null;

    if (scope && this.dedupe.seen(key || name, scope)) {
      this.logger.info('skipped duplicate', name, key || name);

      return false;
    }

    const properties = this.envelope(name, params, { dedupeKey: key, shared: options.shared === true });
    const delivered = this.transport.send(name, properties);

    // Logged after delivery so the group can name the path the event actually took;
    // which of the two transports carried it is most of what a debug session is asking.
    if (this.logger.enabled) {
      this.logger.group(name, { delivered, via: this.transport.lastPath, properties });
    }

    // Only a delivered event burns the key: a transport that refused the event has to
    // be able to send it again.
    if (delivered && scope) {
      this.dedupe.remember(key || name, scope);
    }

    return delivered;
  }

  /**
   * The SDK command is `identify`. v1 called `identity`, which the SDK drops into a
   * dead queue — that is why identity never reached the service.
   */
  identify(payload) {
    const tdc = this.win && this.win.tdc;

    if (typeof tdc === 'function' && payload && Object.keys(payload).length) {
      tdc('identify', payload);

      return true;
    }

    return false;
  }

  /**
   * Lets any script on the page emit a tracked event without a hard dependency.
   *
   * A target carries at most one intake listener: a bundle enqueued twice would
   * otherwise report every cooperative event twice.
   *
   * @return {Function} removes the listener again.
   */
  listen(target = document) {
    if (target.__trackdaddyIntake) {
      target.removeEventListener(INTAKE_EVENT, target.__trackdaddyIntake);
    }

    const handler = (event) => {
      const detail = (event && event.detail) || {};

      this.track(detail.name, detail.params || {}, detail.options || {});
    };

    target.__trackdaddyIntake = handler;
    target.addEventListener(INTAKE_EVENT, handler);

    return () => {
      target.removeEventListener(INTAKE_EVENT, handler);

      if (target.__trackdaddyIntake === handler) {
        delete target.__trackdaddyIntake;
      }
    };
  }
}
